import React from 'react';
import { User, Calendar, Clock, SlidersHorizontal, Copy, Check, RefreshCw, Power, Lock, AlertTriangle, Settings } from 'lucide-react';
import type { PartnerPortalAccess } from '../../../lib/types';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';
import { permissionsSummary, formatLastAccess, formatCreatedAt, formatLockedUntil, isAccessLocked } from './partnerPortalAccessHelpers';
import { GhostAction } from './GhostAction';
import { CredentialsCallout } from './CredentialsCallout';

export interface PartnerPortalAccessDetailsProps {
  access: PartnerPortalAccess;
  sessionPassword: string | null;
  copiedField: 'credentials' | 'username' | null;
  busy: boolean;
  onCopyUsername(): void;
  onCopySessionPassword(): void;
  onNewPassword(): void;
  onToggleActive(): void;
  onEdit(): void;
}

/**
 * Contenuto della sezione "Portale partner" quando esiste un accesso: callout
 * credenziali (se appena generate), proprietà (bloccato/tentativi in testa se
 * bloccato, poi username, creato, ultimo accesso, permessi) e azioni fantasma.
 * Solo presentazione: la logica resta nel container `PartnerPortalSection`.
 */
export function PartnerPortalAccessDetails({
  access,
  sessionPassword,
  copiedField,
  busy,
  onCopyUsername,
  onCopySessionPassword,
  onNewPassword,
  onToggleActive,
  onEdit
}: PartnerPortalAccessDetailsProps): JSX.Element {
  const locked = isAccessLocked(access);

  return (
    <>
      {sessionPassword && <CredentialsCallout password={sessionPassword} onCopy={onCopySessionPassword} />}

      {!access.is_active && (
        <div className="px-1.5 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
          L'accesso è sospeso: il partner non può entrare nel portale.
        </div>
      )}

      <div className="flex flex-col">
        {locked && (
          <>
            <PropRow icon={<Lock className="h-3 w-3" strokeWidth={1.5} />} label="Bloccato fino a">
              <span className="text-red-500 dark:text-red-400">{formatLockedUntil(access.locked_until as string)}</span>
            </PropRow>
            <PropRow icon={<AlertTriangle className="h-3 w-3" strokeWidth={1.5} />} label="Tentativi">
              {access.login_attempts} accessi falliti
            </PropRow>
          </>
        )}
        <PropRow
          icon={<User className="h-3 w-3" strokeWidth={1.5} />}
          label="Username"
          trailing={copiedField === 'username' ? <Check className="h-3 w-3" strokeWidth={1.5} /> : <Copy className="h-3 w-3" strokeWidth={1.5} />}
          onClick={onCopyUsername}
        >
          <span className="font-mono text-[12px]">{access.username}</span>
        </PropRow>
        <PropRow icon={<Calendar className="h-3 w-3" strokeWidth={1.5} />} label="Creato">
          {formatCreatedAt(access.created_at)}
        </PropRow>
        <PropRow icon={<Clock className="h-3 w-3" strokeWidth={1.5} />} label="Ultimo accesso">
          <span className={access.last_login_at ? '' : 'text-[rgba(55,53,47,0.45)] dark:text-white/40'}>
            {formatLastAccess(access.last_login_at)}
          </span>
        </PropRow>
        <PropRow icon={<SlidersHorizontal className="h-3 w-3" strokeWidth={1.5} />} label="Permessi">
          {permissionsSummary(access.permissions)}
        </PropRow>
      </div>

      <div className="flex flex-wrap gap-0.5 pt-0.5">
        <GhostAction
          icon={<Settings className="h-3 w-3" strokeWidth={1.5} />}
          label="Modifica"
          onClick={onEdit}
          disabled={busy}
        />
        <GhostAction
          icon={<RefreshCw className="h-3 w-3" strokeWidth={1.5} />}
          label={locked ? 'Sblocca e genera nuova password' : 'Nuova password'}
          onClick={onNewPassword}
          disabled={busy}
        />
        <GhostAction
          icon={<Power className="h-3 w-3" strokeWidth={1.5} />}
          label={access.is_active ? 'Disattiva' : 'Riattiva'}
          onClick={onToggleActive}
          disabled={busy}
          danger={access.is_active}
        />
      </div>
    </>
  );
}
